// Procedural fighter rig: a pose is a handful of joint positions (facing
// right, units, origin on the ground under the hips). Poses blend linearly
// and are drawn as one ink silhouette.

export type Vec = readonly [number, number];

export interface Pose {
  hip: Vec;
  shoulder: Vec;
  head: Vec;
  fElbow: Vec;
  fHand: Vec;
  bElbow: Vec;
  bHand: Vec;
  fKnee: Vec;
  fFoot: Vec;
  bKnee: Vec;
  bFoot: Vec;
  /** Rear heel lift in radians (Shukokai strikes drive off the ball of the rear foot). */
  heel?: number;
  /** Front heel lift in radians (an opponent's foot rolling off the floor as it walks). */
  fHeel?: number;
  /**
   * Torso rotation: -1 = hanmi (side-on, lead shoulder and hip ahead),
   * 0 = square, +1 = rotated through (rear shoulder driving forward).
   * Spreads the two shoulders and hip joints so the turn reads in silhouette.
   */
  turn?: number;
  /**
   * How a kicking foot off the floor sits on its shin: 0 = toes pulled back,
   * the ball of the foot leading (mae-geri); 1 = pointed along the shin, the
   * instep and shin leading (the roundhouses). Unset: drawn flat, as on the floor.
   */
  kickFoot?: number;
  /**
   * The front foot turned on the floor, on its ball: 0 = toes forward, 1 =
   * turned right round, heel toward the opponent (the standing foot of a
   * rear-leg roundhouse).
   */
  pivot?: number;
  /**
   * Where the belt's knot sits across the front of the belt (0 the middle, the
   * belly toward us; 1 its front edge), where the hips show it other than
   * `turn` does: mawashi-geri's hips turning toward us as the rear leg kicks.
   */
  knot?: number;
  /**
   * Stepped through: 1 = the legs have changed places (the rear one, `b`, now
   * leading), so each hangs from the other side of the hips. Unset: as labelled.
   */
  swap?: number;
}

export type Joint = Exclude<
  keyof Pose,
  'heel' | 'fHeel' | 'turn' | 'kickFoot' | 'pivot' | 'swap' | 'knot'
>;
const JOINTS: Joint[] = [
  'hip',
  'shoulder',
  'head',
  'fElbow',
  'fHand',
  'bElbow',
  'bHand',
  'fKnee',
  'fFoot',
  'bKnee',
  'bFoot',
];

/**
 * The player's Shukokai techniques (Tani-ha Shitō-ryū). Upright, natural
 * kamae with both feet flat; punches driven by the "double hip", the rear
 * foot on the floor as they land, with the pulling hand to the chest; kicks
 * through a high chamber with the guard kept up, the body's weight over or a
 * little in front of the standing leg.
 */
export const SHUKOKAI = {
  // Low, upright kamae: front knee over the toes, side-on, rear heel down,
  // the rear knee bent just a little (the user's call: never straight)
  kamae: {
    hip: [0, 0.72],
    shoulder: [0, 1.22],
    head: [0, 1.41],
    fElbow: [0.24, 1.1],
    fHand: [0.46, 1.2],
    bElbow: [0.04, 1.0],
    bHand: [0.18, 1.16],
    fKnee: [0.3, 0.4],
    fFoot: [0.34, 0],
    bKnee: [-0.17, 0.35],
    bFoot: [-0.36, 0],
    turn: -0.8,
  },
  // Reverse punch: hips and shoulders rotate through, sinking onto a deeply
  // bent front knee over the toes (a slightly shorter stance), the rear foot
  // flat on the floor as it lands; the rear shoulder drives the fist out level
  // (shoulders stay down), lead hand pulled back to the ribs
  gyakuZuki: {
    hip: [0.1, 0.63],
    shoulder: [0.12, 1.06],
    head: [0.14, 1.28],
    fElbow: [-0.1, 0.92],
    fHand: [0.14, 0.87],
    bElbow: [0.4, 1.04],
    bHand: [0.68, 1.01],
    fKnee: [0.49, 0.35],
    fFoot: [0.42, 0],
    bKnee: [-0.11, 0.33],
    bFoot: [-0.36, 0],
    heel: 0,
    turn: 0.75,
  },
  // Maeken-zuki, the lead-hand punch with a shuffle: the weight sinks into it
  // (never rises), the rear foot flat on the floor; side-on so the lead
  // shoulder reaches
  maekenZuki: {
    hip: [0.14, 0.65],
    shoulder: [0.15, 1.11],
    head: [0.16, 1.33],
    fElbow: [0.41, 1.17],
    fHand: [0.66, 1.24],
    bElbow: [-0.07, 0.95],
    bHand: [0.21, 0.92],
    fKnee: [0.48, 0.39],
    fFoot: [0.44, 0],
    bKnee: [-0.08, 0.33],
    bFoot: [-0.27, 0],
    heel: 0,
    turn: -1,
  },
  // Ippon-ashi-dachi-zuki: an oi-zuki that lands early, mid-step. The body's
  // weight has gone over and in front of the planted front foot, the rear leg
  // is passing (its knee by the front knee, its foot still behind and off the
  // floor) and the hand on its side punches, the hips turning through; the
  // foot then sets down in front
  ipponAshiDachiZuki: {
    hip: [0.38, 0.8],
    shoulder: [0.44, 1.25],
    head: [0.44, 1.48],
    fElbow: [0.22, 1.08],
    fHand: [0.42, 1.1],
    bElbow: [0.74, 1.21],
    bHand: [1.03, 1.18],
    fKnee: [0.44, 0.41],
    fFoot: [0.34, 0],
    bKnee: [0.6, 0.44],
    bFoot: [0.26, 0.16],
    heel: 0.5,
    turn: 0.75,
  },
  // Before any kick the knees bend: a short sink that loads the legs, heels down
  kickLoad: {
    hip: [0, 0.65],
    shoulder: [0.02, 1.15],
    head: [0.03, 1.34],
    fElbow: [0.22, 1.04],
    fHand: [0.42, 1.14],
    bElbow: [0.04, 0.94],
    bHand: [0.18, 1.1],
    fKnee: [0.32, 0.35],
    fFoot: [0.34, 0],
    bKnee: [-0.18, 0.31],
    bFoot: [-0.36, 0],
    turn: -0.6,
  },
  // Mae-geri comes off the rear leg: the hips drive forward over and just past
  // the lead foot as the rear knee comes up through, toes pulled back
  maeGeriChamber: {
    hip: [0.36, 0.76],
    shoulder: [0.3, 1.26],
    head: [0.32, 1.45],
    fElbow: [0.48, 1.1],
    fHand: [0.68, 1.22],
    bElbow: [0.19, 0.98],
    bHand: [0.39, 1.09],
    fKnee: [0.31, 0.42],
    fFoot: [0.21, 0],
    bKnee: [0.68, 0.82],
    bFoot: [0.44, 0.53],
    heel: 0,
    turn: 0.3,
    kickFoot: 0,
  },
  // Front snap kick with the ball of the foot (as the user set it in the pose
  // viewer): hips pushed through, the upper body upright and a little in
  // front of the standing foot, the toes partly pulled back
  maeGeri: {
    hip: [0.46, 0.8],
    shoulder: [0.43, 1.3],
    head: [0.44, 1.48],
    fElbow: [0.56, 1.12],
    fHand: [0.7, 1.25],
    bElbow: [0.25, 1.01],
    bHand: [0.47, 1.16],
    fKnee: [0.38, 0.44],
    fFoot: [0.26, 0],
    bKnee: [0.88, 0.82],
    bFoot: [1.24, 0.81],
    heel: 0,
    turn: 0.4,
    kickFoot: 0.6,
  },
  // Roundhouse off the rear leg (the user's call): the hips turn over the
  // planted front foot, which pivots on its ball (`pivot`) until the body is
  // side-on, the kicking hip toward the opponent, the hips turning toward us
  // (the belt's knot coming round to the middle, `knot`); the foot pointed
  mawashiGeriChamber: {
    hip: [0.17, 0.81],
    shoulder: [0.13, 1.27],
    head: [0.16, 1.49],
    fElbow: [-0.14, 1.02],
    fHand: [0, 1.06],
    bElbow: [0.3, 1.14],
    bHand: [0.47, 1.26],
    fKnee: [0.08, 0.4],
    fFoot: [-0.02, 0],
    bKnee: [0.47, 1.07],
    bFoot: [0.36, 0.72],
    turn: 0.9,
    kickFoot: 1,
    pivot: 0.4,
    knot: 0.15,
  },
  // As the user set it (relabelled when it became a rear-leg kick): the upper
  // body upright over and a little in front of the standing foot, the foot
  // between pointed and pulled back
  mawashiGeri: {
    hip: [0.2, 0.8],
    shoulder: [0.05, 1.23],
    head: [0.06, 1.44],
    fElbow: [-0.1, 1.02],
    fHand: [0.16, 1.07],
    bElbow: [0.37, 1.15],
    bHand: [0.47, 1.33],
    fKnee: [0.01, 0.47],
    fFoot: [-0.08, 0],
    bKnee: [0.56, 1.01],
    bFoot: [0.92, 1.18],
    heel: 0,
    turn: 1,
    kickFoot: 0.65,
    pivot: 0.75,
    knot: 0,
  },
  // Maeashi-geri: a low roundhouse onto the thigh. The standing foot doesn't
  // pivot (unlike mawashi-geri); the kicking leg goes out straight and down at
  // the angle its extended pose sets, the shin coming onto the thigh and the
  // foot on past it. Off the lead leg without a skip in: the rear foot stays
  // where kamae has it, the weight shifting back over it…
  maeashiGeriChamber: {
    hip: [0.15, 0.75],
    shoulder: [0.15, 1.24],
    head: [0.15, 1.44],
    fElbow: [0.24, 1.08],
    fHand: [0.36, 1.21],
    bElbow: [-0.04, 1],
    bHand: [0.13, 1.03],
    fKnee: [0.5, 0.65],
    fFoot: [0.24, 0.44],
    bKnee: [0.15, 0.39],
    bFoot: [-0.02, 0],
    turn: -0.7,
    kickFoot: 1,
  },
  maeashiGeri: {
    hip: [0.2, 0.78],
    shoulder: [0.18, 1.23],
    head: [0.2, 1.43],
    fElbow: [0.28, 1.06],
    fHand: [0.48, 1.14],
    bElbow: [-0.02, 1.04],
    bHand: [0.12, 1.01],
    fKnee: [0.58, 0.61],
    fFoot: [0.88, 0.43],
    bKnee: [0.16, 0.4],
    bFoot: [-0.01, 0],
    turn: -0.7,
    kickFoot: 1,
  },
  // …or off the rear leg, its knee coming through past the front leg
  maeashiGeriRearChamber: {
    hip: [0.3, 0.76],
    shoulder: [0.34, 1.25],
    head: [0.36, 1.44],
    fElbow: [0.5, 1.08],
    fHand: [0.64, 1.21],
    bElbow: [0.26, 0.99],
    bHand: [0.4, 1.14],
    fKnee: [0.3, 0.39],
    fFoot: [0.17, 0],
    bKnee: [0.62, 0.71],
    bFoot: [0.4, 0.45],
    heel: 0,
    turn: 0.3,
    kickFoot: 1,
  },
  maeashiGeriRear: {
    hip: [0.53, 0.74],
    shoulder: [0.52, 1.21],
    head: [0.52, 1.41],
    fElbow: [0.68, 1.06],
    fHand: [0.81, 1.19],
    bElbow: [0.29, 0.99],
    bHand: [0.45, 0.99],
    fKnee: [0.46, 0.38],
    fFoot: [0.29, 0],
    bKnee: [0.85, 0.57],
    bFoot: [1.15, 0.39],
    heel: 0,
    turn: 0.3,
    kickFoot: 1,
  },
} satisfies Record<string, Pose>;

export type Technique =
  | 'gyakuZuki'
  | 'maekenZuki'
  | 'ipponAshiDachiZuki'
  | 'maeGeri'
  | 'mawashiGeri'
  | 'maeashiGeri'
  | 'maeashiGeriRear';

/** Kicks pass through a chamber; punches go straight out. */
export const CHAMBER: Partial<Record<Technique, Pose>> = {
  maeGeri: SHUKOKAI.maeGeriChamber,
  mawashiGeri: SHUKOKAI.mawashiGeriChamber,
  maeashiGeri: SHUKOKAI.maeashiGeriChamber,
  maeashiGeriRear: SHUKOKAI.maeashiGeriRearChamber,
};

/**
 * How a kick's lower leg unfolds: `fold` rotates the shin around the knee at
 * its full length (front kicks, which open in the picture plane);
 * `arc` sweeps the foot from behind the knee past it and out (a roundhouse,
 * whose shin whips around the body, foreshortening as it turns past the knee).
 */
export type KickPath = 'fold' | 'arc';

/** How short a roundhouse's shin gets as it turns past the knee (of its length). */
const ARC_SHIN = 0.6;

/**
 * Chamber → extension of a kick: the knee leads (it reaches its mark in the
 * first half, the thigh turning about the hip) and only then the lower leg
 * snaps out from it along `path`, fastest as it lands. `leg` is the kicking leg.
 */
export function kickExtend(
  chamber: Pose,
  target: Pose,
  t: number,
  path: KickPath,
  leg: 'front' | 'rear' = 'front',
): Pose {
  const kj = leg === 'front' ? 'fKnee' : 'bKnee';
  const fj = leg === 'front' ? 'fFoot' : 'bFoot';
  const u = Math.max(0, Math.min(1, t));
  const knee = smooth(u / 0.5);
  const s = Math.max(0, (u - 0.3) / 0.7);
  const snap = s * s;
  // The rest of the body and the other limbs keep their shape as they go
  const out = swing(chamber, target, knee);
  const side = leg === 'front' ? -1 : 1;
  const root = legRoot(out, side);
  const thigh = turned(
    sub(chamber[kj], legRoot(chamber, side)),
    sub(target[kj], legRoot(target, side)),
    knee,
  );
  out[kj] = [root[0] + thigh[0], root[1] + thigh[1]];
  const [kx, ky] = out[kj];
  const dx0 = chamber[fj][0] - chamber[kj][0];
  const dy0 = chamber[fj][1] - chamber[kj][1];
  const dx1 = target[fj][0] - target[kj][0];
  const dy1 = target[fj][1] - target[kj][1];
  if (path === 'fold') {
    const a0 = Math.atan2(dy0, dx0);
    let a1 = Math.atan2(dy1, dx1);
    // The foot always swings forward and up under the knee
    while (a1 < a0) a1 += 2 * Math.PI;
    const a = a0 + (a1 - a0) * snap;
    const l = Math.hypot(dx0, dy0) + (Math.hypot(dx1, dy1) - Math.hypot(dx0, dy0)) * snap;
    out[fj] = [kx + Math.cos(a) * l, ky + Math.sin(a) * l];
  } else {
    // Seen from the side, the shin whipping round foreshortens as it passes
    // the knee: the foot goes by it on the side its straight path does, never
    // nearer than ARC_SHIN of the shin's length
    const [cx, cy] = [dx1 - dx0, dy1 - dy0];
    const w = Math.max(0, Math.min(1, -(dx0 * cx + dy0 * cy) / (cx * cx + cy * cy || 1)));
    const [nx, ny] = [dx0 + cx * w, dy0 + cy * w];
    const near = Math.hypot(nx, ny) || 1e-9;
    const shin = Math.hypot(dx0, dy0) + (Math.hypot(dx1, dy1) - Math.hypot(dx0, dy0)) * w;
    const out_ = Math.max(0, shin * ARC_SHIN - near) / near;
    const bulge = (out_ * Math.sin(Math.PI * snap)) / Math.max(0.5, Math.sin(Math.PI * w));
    out[fj] = [kx + dx0 + cx * snap + nx * bulge, ky + dy0 + cy * snap + ny * bulge];
  }
  return out;
}

/**
 * `pose` with the kicking leg swung about the hip until its foot is at height
 * `aim`; the leg keeps its shape and length.
 */
export function aimKick(pose: Pose, aim: number, leg: 'front' | 'rear' = 'front'): Pose {
  return swingTo(pose, pose.hip, leg === 'front' ? ['fKnee', 'fFoot'] : ['bKnee', 'bFoot'], aim);
}

/**
 * `pose` with the punching arm swung about the shoulder until its fist is at
 * height `aim` (a low opponent's face); the arm keeps its shape and length.
 */
export function aimPunch(pose: Pose, aim: number, arm: 'front' | 'rear'): Pose {
  return swingTo(
    pose,
    pose.shoulder,
    arm === 'front' ? ['fElbow', 'fHand'] : ['bElbow', 'bHand'],
    aim,
  );
}

/** Swing a limb (`kj` middle joint, `fj` end) about `pivot` until its end is at height `aim`. */
function swingTo(pose: Pose, pivot: Vec, [kj, fj]: [Joint, Joint], aim: number): Pose {
  const [hx, hy] = pivot;
  const [fx, fy] = pose[fj];
  const r = Math.hypot(fx - hx, fy - hy);
  const s = Math.max(-1, Math.min(1, (aim - hy) / r));
  const a = Math.asin(s) - Math.atan2(fy - hy, fx - hx);
  const c = Math.cos(a);
  const sn = Math.sin(a);
  const swing = ([x, y]: Vec): Vec => [
    hx + (x - hx) * c - (y - hy) * sn,
    hy + (x - hx) * sn + (y - hy) * c,
  ];
  return { ...pose, [kj]: swing(pose[kj]), [fj]: swing(pose[fj]) };
}

/**
 * `pose` after stepping `d` forward into the opposite stance: the legs swap
 * roles (the old rear leg now leads), so the result draws exactly like
 * `pose` shifted by `d`, with the new rear heel down.
 */
export function steppedThrough(pose: Pose, d: number): Pose {
  const at = (v: Vec): Vec => [v[0] + d, v[1]];
  return {
    ...pose,
    hip: at(pose.hip),
    shoulder: at(pose.shoulder),
    head: at(pose.head),
    fElbow: at(pose.fElbow),
    fHand: at(pose.fHand),
    bElbow: at(pose.bElbow),
    bHand: at(pose.bHand),
    fKnee: at(pose.bKnee),
    fFoot: at(pose.bFoot),
    bKnee: at(pose.fKnee),
    bFoot: at(pose.fFoot),
    heel: 0,
    swap: 1 - (pose.swap ?? 0),
  };
}

/**
 * Stepping back `d` to the spot (`u` 0 → 1) after setting the rear leg down a
 * stance ahead: that foot steps back past the planted one to its place in
 * `pose` (the rest pose, which it ends in), lifted just clear of the floor,
 * as the hips go back over the planted foot. Returns the pose and how far the
 * body is still ahead of the spot.
 */
export function stepBack(pose: Pose, d: number, u: number): { pose: Pose; ahead: number } {
  const s = smooth(u);
  const ahead = d * (1 - s);
  // Where it starts: `pose` with its legs exchanged, the stepping one in front
  const from = steppedThrough(pose, 0);
  const [fx, bx] = [pose.fFoot[0], pose.bFoot[0]];
  const moved: Pose = {
    ...blend(from, pose, s),
    fFoot: [fx - ahead, 0],
    bFoot: [fx + d + (bx - fx - d) * s - ahead, 0.07 * Math.sin(Math.PI * s)],
  };
  // The knees bend to suit, each leg going from one shape to the other
  return { ahead, pose: fit(moved, mixLimbs(limbsOf(from), limbsOf(pose), s)) };
}

/**
 * The karateka's bounce in kamae, and an opponent's waiting in guard: sinking
 * into the knees and back up to `p`,
 * never above it, so the rear leg never straightens (the user saw it go
 * straight and bent by turns), and easing forward over the front knee as it
 * sinks, so the rear leg hardly moves (the user's call); `k` eases it in.
 */
export function bounceDown(p: Pose, time: number, k = 1): Pose {
  const d = -k * 0.009 * (1 - Math.cos(time * 8));
  return fit(nudge(p, UPPER, -1.5 * d, d), limbsOf(p));
}

/**
 * The street opponents: each type has a guard it advances behind, a windup
 * that telegraphs its attack, and the attack itself (shown when it lands).
 * Facing right, towards the player.
 */
export const STREET = {
  // Each opponent is one big shape, readable at phone size; its tell (the
  // windup) changes the outline before the hit.
  // Brawler: the plain rectangle every other shape is measured against;
  // hood up, leaning in, fists at the chest
  basicGuard: {
    hip: [0, 0.74],
    shoulder: [0.1, 1.22],
    head: [0.16, 1.38],
    fElbow: [0.24, 1.02],
    fHand: [0.36, 1.2],
    bElbow: [0.06, 1.0],
    bHand: [0.22, 1.16],
    fKnee: [0.22, 0.4],
    fFoot: [0.32, 0],
    bKnee: [-0.14, 0.38],
    bFoot: [-0.3, 0],
  },
  // The haymaker cocked far back: a spike behind him, lead arm out to measure
  basicWindup: {
    hip: [-0.04, 0.74],
    shoulder: [-0.02, 1.22],
    head: [0.06, 1.38],
    fElbow: [0.22, 1.1],
    fHand: [0.38, 1.2],
    bElbow: [-0.3, 1.18],
    bHand: [-0.46, 1.3],
    fKnee: [0.2, 0.42],
    fFoot: [0.34, 0],
    bKnee: [-0.2, 0.36],
    bFoot: [-0.34, 0],
  },
  basicAttack: {
    hip: [0.12, 0.74],
    shoulder: [0.22, 1.22],
    head: [0.3, 1.36],
    fElbow: [0.1, 1.04],
    fHand: [0.16, 1.2],
    bElbow: [0.44, 1.32],
    bHand: [0.72, 1.3],
    fKnee: [0.38, 0.4],
    fFoot: [0.48, 0],
    bKnee: [-0.08, 0.36],
    bFoot: [-0.24, 0],
  },
  // Bruiser: a pear, small bearded head on a huge gut, short thick legs
  heavyGuard: {
    hip: [0, 0.66],
    shoulder: [0.06, 1.18],
    head: [0.12, 1.36],
    fElbow: [0.3, 1.0],
    fHand: [0.38, 1.14],
    bElbow: [0.04, 0.96],
    bHand: [0.2, 1.1],
    fKnee: [0.24, 0.34],
    fFoot: [0.38, 0],
    bKnee: [-0.2, 0.32],
    bFoot: [-0.38, 0],
  },
  // Both fists clasped overhead for a hammer blow, elbows out wide so the
  // arms frame the head instead of stacking on it
  heavyWindup: {
    hip: [-0.02, 0.68],
    shoulder: [-0.06, 1.2],
    head: [0, 1.37],
    fElbow: [0.3, 1.5],
    fHand: [0.04, 1.76],
    bElbow: [-0.32, 1.48],
    bHand: [-0.06, 1.75],
    fKnee: [0.22, 0.36],
    fFoot: [0.36, 0],
    bKnee: [-0.2, 0.34],
    bFoot: [-0.36, 0],
  },
  heavyAttack: {
    hip: [0.12, 0.62],
    shoulder: [0.26, 1.06],
    head: [0.34, 1.18],
    fElbow: [0.48, 1.08],
    fHand: [0.66, 0.92],
    bElbow: [0.42, 1.02],
    bHand: [0.62, 0.88],
    fKnee: [0.42, 0.3],
    fFoot: [0.5, 0],
    bKnee: [-0.1, 0.28],
    bFoot: [-0.3, 0],
  },
  // After the first hit: bent over, both hands on the gut; one more lands it
  heavyHurt: {
    hip: [-0.02, 0.62],
    shoulder: [0.16, 1.06],
    head: [0.28, 1.14],
    fElbow: [0.28, 0.96],
    fHand: [0.36, 0.84],
    bElbow: [0.14, 0.9],
    bHand: [0.3, 0.76],
    fKnee: [0.28, 0.3],
    fFoot: [0.4, 0],
    bKnee: [-0.18, 0.28],
    bFoot: [-0.36, 0],
  },
  // Feinter: thin, leaning back on his toes, hands low and loose
  feinterGuard: {
    hip: [0, 0.78],
    shoulder: [-0.05, 1.28],
    head: [-0.03, 1.46],
    fElbow: [0.12, 1.04],
    fHand: [0.3, 0.98],
    bElbow: [-0.08, 1.04],
    bHand: [0.06, 1.08],
    fKnee: [0.2, 0.42],
    fFoot: [0.3, 0],
    bKnee: [-0.14, 0.42],
    bFoot: [-0.3, 0.03],
    heel: 0.5,
  },
  // The fake: a stamp forward with a shoulder dip, but the hands stay low
  feinterFeint: {
    hip: [0.1, 0.72],
    shoulder: [0.18, 1.2],
    head: [0.24, 1.36],
    fElbow: [0.32, 0.98],
    fHand: [0.46, 0.9],
    bElbow: [0.06, 0.98],
    bHand: [0.2, 1.02],
    fKnee: [0.4, 0.36],
    fFoot: [0.5, 0],
    bKnee: [-0.08, 0.38],
    bFoot: [-0.24, 0.02],
    heel: 0.4,
  },
  // The real rush: the hands come up
  feinterWindup: {
    hip: [-0.02, 0.72],
    shoulder: [0, 1.2],
    head: [0.04, 1.38],
    fElbow: [0.22, 1.08],
    fHand: [0.34, 1.26],
    bElbow: [-0.1, 1.14],
    bHand: [0, 1.32],
    fKnee: [0.24, 0.38],
    fFoot: [0.34, 0],
    bKnee: [-0.18, 0.34],
    bFoot: [-0.32, 0],
  },
  feinterAttack: {
    hip: [0.1, 0.74],
    shoulder: [0.2, 1.22],
    head: [0.26, 1.4],
    fElbow: [0.08, 1.06],
    fHand: [0.2, 1.26],
    bElbow: [0.44, 1.26],
    bHand: [0.72, 1.28],
    fKnee: [0.36, 0.4],
    fFoot: [0.46, 0],
    bKnee: [-0.1, 0.38],
    bFoot: [-0.3, 0.03],
  },
  // Boxer: a closed block, chin tucked, lead glove up before the face, rear
  // glove at the chin, elbows on the ribs: no daylight anywhere
  blockerGuard: {
    hip: [0, 0.74],
    shoulder: [0.06, 1.22],
    head: [0.1, 1.38],
    fElbow: [0.26, 1.12],
    fHand: [0.38, 1.38],
    bElbow: [0.12, 1.04],
    bHand: [0.26, 1.26],
    fKnee: [0.22, 0.4],
    fFoot: [0.32, 0],
    bKnee: [-0.14, 0.38],
    bFoot: [-0.28, 0],
  },
  // The rear glove pulls back behind the head to load the right: the block
  // opens and the chin is bare
  blockerWindup: {
    hip: [-0.02, 0.74],
    shoulder: [-0.02, 1.22],
    head: [0.06, 1.4],
    fElbow: [0.26, 1.12],
    fHand: [0.38, 1.38],
    bElbow: [-0.24, 1.12],
    bHand: [-0.18, 1.34],
    fKnee: [0.22, 0.4],
    fFoot: [0.32, 0],
    bKnee: [-0.16, 0.38],
    bFoot: [-0.3, 0],
  },
  blockerAttack: {
    hip: [0.1, 0.74],
    shoulder: [0.2, 1.24],
    head: [0.24, 1.42],
    fElbow: [0.24, 1.12],
    fHand: [0.32, 1.32],
    bElbow: [0.46, 1.3],
    bHand: [0.74, 1.32],
    fKnee: [0.36, 0.4],
    fFoot: [0.46, 0],
    bKnee: [-0.12, 0.38],
    bFoot: [-0.32, 0],
  },
  // Kickboxer: the tall vertical line, long legs, up on his toes, hands high
  kickerGuard: {
    hip: [0, 0.86],
    shoulder: [0.02, 1.34],
    head: [0.04, 1.53],
    fElbow: [0.16, 1.18],
    fHand: [0.24, 1.38],
    bElbow: [0.02, 1.14],
    bHand: [0.12, 1.36],
    fKnee: [0.16, 0.46],
    fFoot: [0.24, 0],
    bKnee: [-0.12, 0.46],
    bFoot: [-0.24, 0.02],
    heel: 0.4,
  },
  // Chambered out of range, the knee up to the chest: the line turns into a 7
  kickerWindup: {
    hip: [-0.02, 0.88],
    shoulder: [-0.08, 1.36],
    head: [-0.08, 1.55],
    fElbow: [0.1, 1.2],
    fHand: [0.2, 1.38],
    bElbow: [-0.22, 1.16],
    bHand: [-0.12, 1.34],
    fKnee: [0.3, 1.22],
    fFoot: [0.06, 1.06],
    bKnee: [0, 0.46],
    bFoot: [-0.04, 0],
  },
  // Roundhouse landing at head height
  kickerAttack: {
    hip: [0.04, 0.9],
    shoulder: [-0.16, 1.34],
    head: [-0.17, 1.53],
    fElbow: [0.04, 1.2],
    fHand: [0.16, 1.36],
    bElbow: [-0.32, 1.12],
    bHand: [-0.26, 1.28],
    fKnee: [0.46, 1.2],
    fFoot: [0.92, 1.44],
    bKnee: [0.04, 0.47],
    bFoot: [-0.02, 0],
  },
  // Wrestler: the only horizontal shape, a low wedge, arms spread like pincers
  grapplerGuard: {
    hip: [0, 0.56],
    shoulder: [0.36, 0.94],
    head: [0.52, 1.04],
    fElbow: [0.56, 0.86],
    fHand: [0.72, 0.98],
    bElbow: [0.42, 0.76],
    bHand: [0.64, 0.74],
    fKnee: [0.36, 0.3],
    fFoot: [0.46, 0],
    bKnee: [-0.2, 0.28],
    bFoot: [-0.44, 0],
  },
  // The level change: he drops even lower before he shoots in
  grapplerWindup: {
    hip: [-0.02, 0.44],
    shoulder: [0.36, 0.78],
    head: [0.52, 0.88],
    fElbow: [0.54, 0.7],
    fHand: [0.7, 0.78],
    bElbow: [0.38, 0.6],
    bHand: [0.6, 0.58],
    fKnee: [0.36, 0.24],
    fFoot: [0.52, 0],
    bKnee: [-0.24, 0.2],
    bFoot: [-0.5, 0],
  },
  // The throw: rising and turning, hauling the opponent over the hip
  grapplerAttack: {
    hip: [0.02, 0.76],
    shoulder: [-0.08, 1.22],
    head: [-0.12, 1.4],
    fElbow: [0.14, 1.36],
    fHand: [0.3, 1.5],
    bElbow: [-0.16, 1.4],
    bHand: [0.02, 1.56],
    fKnee: [0.24, 0.4],
    fFoot: [0.34, 0],
    bKnee: [-0.16, 0.38],
    bFoot: [-0.3, 0],
  },
  // After the first hit: still coming, one arm hanging
  grapplerHurt: {
    hip: [0, 0.56],
    shoulder: [0.36, 0.94],
    head: [0.5, 0.98],
    fElbow: [0.56, 0.86],
    fHand: [0.72, 0.98],
    bElbow: [0.34, 0.66],
    bHand: [0.38, 0.44],
    fKnee: [0.36, 0.3],
    fFoot: [0.46, 0],
    bKnee: [-0.2, 0.28],
    bFoot: [-0.44, 0],
  },
  // Rocked by a hit, he stays low: head snapped back, arms flung up
  grapplerStagger: {
    hip: [-0.06, 0.58],
    shoulder: [0.16, 1.02],
    head: [0.2, 1.18],
    fElbow: [0.36, 1.02],
    fHand: [0.46, 1.16],
    bElbow: [0.02, 0.96],
    bHand: [-0.08, 1.06],
    fKnee: [0.3, 0.3],
    fFoot: [0.44, 0],
    bKnee: [-0.24, 0.28],
    bFoot: [-0.44, 0],
  },
  // Rocked by a hit: head snaps back, arms fly out
  stagger: {
    hip: [-0.08, 0.74],
    shoulder: [-0.24, 1.2],
    head: [-0.34, 1.34],
    fElbow: [-0.02, 1.28],
    fHand: [0.1, 1.42],
    bElbow: [-0.38, 1.12],
    bHand: [-0.46, 1.26],
    fKnee: [0.14, 0.4],
    fFoot: [0.3, 0],
    bKnee: [-0.22, 0.38],
    bFoot: [-0.34, 0],
  },
  // Knocked down: flat on the back, head away from the player, one knee up
  // and one arm flung out so the limbs read apart
  down: {
    hip: [0, 0.12],
    shoulder: [-0.5, 0.15],
    head: [-0.72, 0.14],
    fElbow: [-0.3, 0.07],
    fHand: [-0.08, 0.08],
    bElbow: [-0.78, 0.34],
    bHand: [-0.98, 0.46],
    fKnee: [0.26, 0.44],
    fFoot: [0.5, 0.02],
    bKnee: [0.38, 0.1],
    bFoot: [0.76, 0.03],
  },
} satisfies Record<string, Pose>;

/** The joints above the legs. */
export const UPPER: readonly Joint[] = [
  'hip',
  'shoulder',
  'head',
  'fElbow',
  'fHand',
  'bElbow',
  'bHand',
];

/** `pose` with `joints` moved by (dx, dy). */
export function nudge(pose: Pose, joints: readonly Joint[], dx: number, dy: number): Pose {
  const out = { ...pose };
  for (const j of joints) out[j] = [pose[j][0] + dx, pose[j][1] + dy];
  return out;
}

export const POSES = {
  stance: {
    hip: [0, 0.78],
    shoulder: [0.05, 1.28],
    head: [0.08, 1.47],
    fElbow: [0.22, 1.1],
    fHand: [0.36, 1.22],
    bElbow: [-0.08, 1.02],
    bHand: [0.1, 1.08],
    fKnee: [0.22, 0.42],
    fFoot: [0.32, 0],
    bKnee: [-0.14, 0.4],
    bFoot: [-0.3, 0],
  },
  hurt: {
    hip: [-0.06, 0.76],
    shoulder: [-0.22, 1.22],
    head: [-0.32, 1.37],
    fElbow: [0.02, 1.26],
    fHand: [0.14, 1.42],
    bElbow: [-0.4, 1.12],
    bHand: [-0.5, 1.28],
    fKnee: [0.14, 0.4],
    fFoot: [0.26, 0],
    bKnee: [-0.2, 0.38],
    bFoot: [-0.3, 0],
  },
} satisfies Record<string, Pose>;

export type PoseName = keyof typeof POSES;

export function blend(a: Pose, b: Pose, t: number): Pose {
  const out = {} as Record<Joint, Vec>;
  for (const j of JOINTS) {
    out[j] = [a[j][0] + (b[j][0] - a[j][0]) * t, a[j][1] + (b[j][1] - a[j][1]) * t];
  }
  const mix = (x = 0, y = 0): number => x + (y - x) * t;
  // A kicking foot keeps its set while blending to or from a pose without one
  const foot = a.kickFoot ?? b.kickFoot;
  const pivoted = a.pivot !== undefined || b.pivot !== undefined;
  const swapped = a.swap !== undefined || b.swap !== undefined;
  const knotted = a.knot !== undefined || b.knot !== undefined;
  return {
    ...out,
    heel: mix(a.heel, b.heel),
    ...(a.fHeel !== undefined || b.fHeel !== undefined ? { fHeel: mix(a.fHeel, b.fHeel) } : {}),
    turn: mix(a.turn, b.turn),
    ...(foot === undefined ? {} : { kickFoot: mix(a.kickFoot ?? foot, b.kickFoot ?? foot) }),
    ...(pivoted ? { pivot: mix(a.pivot, b.pivot) } : {}),
    // Stepping through, each leg's hip comes round to its new side
    ...(swapped ? { swap: mix(a.swap, b.swap) } : {}),
    ...(knotted ? { knot: mix(knotAt(a), knotAt(b)) } : {}),
  };
}

/**
 * Where a leg hangs from the hips and an arm from the shoulders (`s` 1 for the
 * rear one, -1 for the lead): turning, the hips and shoulders bring one side
 * ahead of the other (`k` the build's bulk).
 */
function legRoot(pose: Pose, s: number, k = 1): Vec {
  const side = s * (1 - 2 * (pose.swap ?? 0));
  return [pose.hip[0] + side * (pose.turn ?? 0) * 0.05 * k, pose.hip[1]];
}
function armRoot(pose: Pose, s: number, k = 1): Vec {
  return [pose.shoulder[0] + s * (pose.turn ?? 0) * 0.075 * k, pose.shoulder[1]];
}

/** 0 → 1 easing in and out (`s` clamped). */
export function smooth(s: number): number {
  const c = Math.max(0, Math.min(1, s));
  return c * c * (3 - 2 * c);
}

const sub = (a: Vec, b: Vec): Vec => [a[0] - b[0], a[1] - b[1]];

/** Vector `a` turned and stretched `t` of the way to `b` (the shorter way round). */
function turned(a: Vec, b: Vec, t: number): Vec {
  const from = Math.atan2(a[1], a[0]);
  let by = Math.atan2(b[1], b[0]) - from;
  if (by > Math.PI) by -= 2 * Math.PI;
  if (by < -Math.PI) by += 2 * Math.PI;
  const len = Math.hypot(a[0], a[1]) + (Math.hypot(b[0], b[1]) - Math.hypot(a[0], a[1])) * t;
  return [Math.cos(from + by * t) * len, Math.sin(from + by * t) * len];
}

/** The limbs: the joint they bend at, the one they end in, where they hang from. */
const LIMBS: readonly { mid: Joint; end: Joint; root: (p: Pose) => Vec }[] = [
  { mid: 'bKnee', end: 'bFoot', root: (p) => legRoot(p, 1) },
  { mid: 'fKnee', end: 'fFoot', root: (p) => legRoot(p, -1) },
  { mid: 'bElbow', end: 'bHand', root: (p) => armRoot(p, 1) },
  { mid: 'fElbow', end: 'fHand', root: (p) => armRoot(p, -1) },
];

/** A limb's shape: its upper and lower bone's lengths and which way it bends (signed). */
export type Limb = readonly [upper: number, lower: number, bend: number];

/** The shape of each of `pose`'s limbs (in LIMBS' order). */
export function limbsOf(pose: Pose): Limb[] {
  return LIMBS.map(({ mid, end, root }) => {
    const [rx, ry] = root(pose);
    const [mx, my] = pose[mid];
    const [ex, ey] = pose[end];
    const upper = Math.hypot(mx - rx, my - ry);
    const reach = Math.hypot(ex - rx, ey - ry);
    // The sine of the bend: which side of root → end the joint sticks out
    const bend = ((ex - rx) * (my - ry) - (ey - ry) * (mx - rx)) / (reach * upper || 1);
    // (a straight limb bends neither way)
    return [upper, Math.hypot(ex - mx, ey - my), Math.abs(bend) < 1e-9 ? 0 : bend];
  });
}

/** A limb bent less than this (the sine of its bend) is as good as straight. */
const STRAIGHT = 0.15;

/**
 * Limb shapes `t` of the way from `a`'s to `b`'s. A limb never changes the
 * side it bends to on the way: one (nearly) straight in one pose bends the way
 * it does in the other; straight in both but either way, it's left as blended.
 */
export function mixLimbs(a: readonly Limb[], b: readonly Limb[], t: number): Limb[] {
  // There, each pose's limbs are just as it has them
  if (t <= 0 || t >= 1) return [...(t <= 0 ? a : b)];
  return a.map((l, i) => {
    const m = b[i]!;
    let [p, q] = [l[2], m[2]];
    if (p * q < 0) {
      if (Math.max(Math.abs(p), Math.abs(q)) < STRAIGHT) p = q = NaN;
      else if (Math.abs(p) < Math.abs(q)) p = 0;
      else q = 0;
    }
    return [l[0] + (m[0] - l[0]) * t, l[1] + (m[1] - l[1]) * t, p + (q - p) * t];
  });
}

/**
 * Where a limb from `root` to `end` bends with bones `upper` and `lower`, on
 * the `side` (±1) of root → end it bends to; one that can't reach goes
 * straight, both bones stretching alike.
 */
function bendAt(root: Vec, end: Vec, upper: number, lower: number, side: number): Vec {
  const dx = end[0] - root[0];
  const dy = end[1] - root[1];
  const d = Math.hypot(dx, dy) || 1e-9;
  const [ux, uy] = [dx / d, dy / d];
  if (d >= upper + lower) {
    const a = (d * upper) / (upper + lower);
    return [root[0] + ux * a, root[1] + uy * a];
  }
  const r = Math.max(d, Math.abs(upper - lower));
  const a = (upper * upper - lower * lower + r * r) / (2 * r);
  const h = Math.sqrt(Math.max(0, upper * upper - a * a)) * side;
  return [root[0] + ux * a - uy * h, root[1] + uy * a + ux * h];
}

/**
 * `pose` with its knees and elbows placed so its limbs have `limbs`' shapes
 * between where they hang from and where their hands and feet are. A knee
 * bends forward unless a pose clearly bends it the other way; an elbow that
 * was straight drops.
 */
export function fit(pose: Pose, limbs: readonly Limb[]): Pose {
  const out = { ...pose };
  LIMBS.forEach(({ mid, end, root }, i) => {
    const [upper, lower, bend] = limbs[i]!;
    if (Number.isNaN(bend)) return;
    const r = root(pose);
    // A hand or a foot off the floor goes no further than the limb reaches
    // (only a foot planted on the floor holds its place)
    const [ex, ey] = pose[end];
    const far = Math.hypot(ex - r[0], ey - r[1]) / (upper + lower);
    if (far > 1 && (i >= 2 || ey >= 0.002)) {
      out[end] = [r[0] + (ex - r[0]) / far, r[1] + (ey - r[1]) / far];
    }
    const side = i < 2 ? (bend < -STRAIGHT ? -1 : 1) : bend > 0 ? 1 : -1;
    out[mid] = bendAt(r, out[end], upper, lower, side);
  });
  return out;
}

/**
 * `a` blended `t` of the way to `b` with the limbs keeping their shape: the
 * hands and feet go straight to where they're going and the knees and elbows
 * bend to suit, each bone changing its length only from one pose's to the
 * other's (a plain blend would shorten a limb swinging past where it hangs from).
 */
export function swing(a: Pose, b: Pose, t: number): Pose {
  return fit(blend(a, b, t), mixLimbs(limbsOf(a), limbsOf(b), t));
}

/** A foot this low counts as on the floor (on its ball, its heel up, too). */
const GROUNDED = 0.03;

/** The bone from `p` to `q`: its angle (radians) and length. */
const bone = (p: Vec, q: Vec): [number, number] => [
  Math.atan2(q[1] - p[1], q[0] - p[0]),
  Math.hypot(q[0] - p[0], q[1] - p[1]),
];

/** Angle `a` turned `t` of the way to `b`, the shorter way round. */
function turnTo(a: number, b: number, t: number): number {
  let d = (b - a) % (2 * Math.PI);
  if (d > Math.PI) d -= 2 * Math.PI;
  else if (d < -Math.PI) d += 2 * Math.PI;
  return a + d * t;
}

/**
 * `a` blended `t` of the way to `b` like `swing`, but each limb off the floor
 * turns its bones about their joints (the shorter way round) instead of taking
 * its hand or foot along a line: a knee drawn up past the hips into a chamber,
 * or an elbow cocked back, keeps the side it bends to, where `swing`, reading
 * that side off the line from hip to foot, flips it over on the way. A foot on
 * the floor in both poses stays on it, as in `swing`; one swept down to the
 * floor stops there.
 */
export function sweep(a: Pose, b: Pose, t: number): Pose {
  const out = swing(a, b, t);
  if (t <= 0 || t >= 1) return out;
  LIMBS.forEach(({ mid, end, root }, i) => {
    const leg = i < 2;
    if (leg && a[end][1] < GROUNDED && b[end][1] < GROUNDED) return;
    const [ua, ub] = [bone(root(a), a[mid]), bone(root(b), b[mid])];
    const [la, lb] = [bone(a[mid], a[end]), bone(b[mid], b[end])];
    const r = root(out);
    const up = turnTo(ua[0], ub[0], t);
    const upper = ua[1] + (ub[1] - ua[1]) * t;
    const lo = turnTo(la[0], lb[0], t);
    const lower = la[1] + (lb[1] - la[1]) * t;
    const m: Vec = [r[0] + Math.cos(up) * upper, r[1] + Math.sin(up) * upper];
    const e: Vec = [m[0] + Math.cos(lo) * lower, m[1] + Math.sin(lo) * lower];
    if (leg && e[1] < 0) {
      // Swept down onto the floor: the foot stops on it, the knee bending to suit
      const side = (e[0] - r[0]) * (m[1] - r[1]) - (e[1] - r[1]) * (m[0] - r[0]) > 0 ? 1 : -1;
      out[end] = [e[0], 0];
      out[mid] = bendAt(r, out[end], upper, lower, side);
    } else {
      out[mid] = m;
      out[end] = e;
    }
  });
  return out;
}

/**
 * `pose` with the body carried `dx` forward (body units) over its planted
 * feet, only as far as the legs reach them, the knees bending to suit; a foot
 * in the air goes along.
 */
export function lunged(pose: Pose, dx: number): Pose {
  if (!dx) return pose;
  const limbs = limbsOf(pose);
  let d = dx;
  for (const [lo, hi] of reachable(pose, limbs)) {
    d = dx > 0 ? Math.max(0, Math.min(d, hi)) : Math.min(0, Math.max(d, lo));
  }
  return fit(carried(pose, d), limbs);
}

/**
 * How far the body can go back or forward (lo, hi: body units) with each
 * planted foot still in reach of its leg, for `limbs`' leg lengths.
 */
function reachable(pose: Pose, limbs: readonly Limb[]): [number, number][] {
  const out: [number, number][] = [];
  for (const [foot, side, i] of LEGS) {
    const [fx, fy] = pose[foot];
    if (fy >= 0.002) continue;
    const [rx, ry] = legRoot(pose, side);
    const [upper, lower] = limbs[i]!;
    const w = Math.sqrt(Math.max(0, (upper + lower) ** 2 - (ry - fy) ** 2));
    out.push([fx - rx - w, fx - rx + w]);
  }
  return out;
}

/** The feet, the side of the hips their legs hang from, their place in LIMBS. */
const LEGS = [
  ['bFoot', 1, 0],
  ['fFoot', -1, 1],
] as const;

/** `pose`'s body carried `d` forward, and any foot in the air with it. */
function carried(pose: Pose, d: number): Pose {
  const out = nudge(pose, UPPER, d, 0);
  for (const foot of ['fFoot', 'bFoot'] as const) {
    if (pose[foot][1] >= 0.002) out[foot] = [pose[foot][0] + d, pose[foot][1]];
  }
  return out;
}

/**
 * `pose` with its limbs given `limbs`' shapes, the body first drawn back or
 * on just enough for every planted leg to reach its foot.
 */
export function reaching(pose: Pose, limbs: readonly Limb[]): Pose {
  let d = 0;
  for (const [lo, hi] of reachable(pose, limbs)) d = Math.max(lo, Math.min(hi, d));
  return fit(d ? carried(pose, d) : pose, limbs);
}

/**
 * `pose` stood on its feet as they are: the hips (and all above them)
 * lowered just as far as each foot on the floor (by `free`, those false in
 * it, if given) needs to stay in its leg's reach, and raised by `rise` only as far as the legs still
 * reach; the knees bending to suit, so the legs keep `limbs`' lengths.
 */
export function standOn(
  pose: Pose,
  limbs: readonly Limb[],
  rise = 0,
  free: { fFoot?: boolean; bFoot?: boolean } = {},
): Pose {
  const dy = standHeight(pose, limbs, rise, free);
  return fit(dy ? nudge(pose, UPPER, 0, dy) : pose, limbs);
}

/** How far `standOn` moves the hips up (+) or down (−), body units. */
export function standHeight(
  pose: Pose,
  limbs: readonly Limb[],
  rise = 0,
  free: { fFoot?: boolean; bFoot?: boolean } = {},
): number {
  let drop = 0;
  let room = Infinity;
  for (const [foot, side, i] of LEGS) {
    const [fx, fy] = pose[foot];
    // (told, a foot a kick is lifting stands on nothing however low it still
    // is, and one on its ball stands on the floor)
    if (free[foot] ?? fy >= 0.002) continue;
    const [rx, ry] = legRoot(pose, side);
    const reach = (limbs[i]![0] + limbs[i]![1]) * 0.995;
    const dx = Math.min(Math.abs(fx - rx), reach);
    // As high as the hips may be over this foot
    const high = fy + Math.sqrt(reach ** 2 - dx ** 2);
    drop = Math.max(drop, ry - high);
    room = Math.min(room, high - ry);
  }
  return drop > 0 ? -drop : Math.min(rise, room);
}

/**
 * How far behind and ahead of the hips (body units, along the floor) a foot
 * on the floor stays within its leg's reach, the hips lowered at most `drop`
 * (its ankle `ankle` up): a foot shoved further is dragged along.
 */
export function legReach(
  pose: Pose,
  limbs: readonly Limb[],
  foot: 'fFoot' | 'bFoot',
  drop: number,
  ankle = 0,
): [number, number] {
  const [, side, i] = LEGS.find(([f]) => f === foot)!;
  const [rx, ry] = legRoot(pose, side);
  const reach = (limbs[i]![0] + limbs[i]![1]) * 0.98;
  // (`ankle` up off the floor, as a foot rolled onto its ball has it)
  const w = Math.sqrt(Math.max(0, reach ** 2 - Math.max(0, ry - drop - ankle) ** 2));
  return [rx - w, rx + w];
}

export type Hair =
  | 'bun'
  | 'short'
  | 'bald'
  | 'beard'
  | 'cap'
  | 'capBack'
  | 'hood'
  | 'headband'
  | 'mohawk'
  | 'flattop'
  | 'hat'
  | 'topknot';
export type Eyes = 'none' | 'calm' | 'angry' | 'hurt';

/** What a fighter wears. Each outfit has its own cut, so it shows in the silhouette (`CUTS`). */
export type Outfit =
  'gi' | 'hoodie' | 'bruiser' | 'tracksuit' | 'boxer' | 'thai' | 'singlet' | 'coat' | 'hakama';

/**
 * A fighter's silhouette: the shape language that tells characters apart
 * when everything is drawn in one ink colour against the light.
 */
export interface Build {
  hair: Hair;
  outfit: Outfit;
  /** Overall thickness; heavies are bigger. */
  bulk: number;
  scale: number;
  /** Shoulder width, 1 = normal; boxers and wrestlers are wider up top. */
  shoulders?: number;
  /** How far the upper back curves out, 1 = normal; the bruiser stands straight. */
  back?: number;
  /** Gut pushing out past the belt line (heavies). */
  belly?: number;
  /** Head size on top of the build's own, 1 = normal; a pear-shaped bruiser's is small. */
  head?: number;
  /** Neck thickness, 1 = normal; above 1 the trapezius slopes into it too. */
  neck?: number;
  /** Belt tails this long, hanging from the knot as part of the silhouette
   *  (an ink black belt reads by its shape, not its colour). */
  tails?: number;
}

/** The player: a karateka in a gi, hair in a bun. */
export const KARATEKA: Build = { hair: 'bun', outfit: 'gi', bulk: 1, scale: 1 };

export interface Paint {
  fill: string;
  /** Belt colour (drawn on top of the silhouette), if any. */
  belt?: string;
  eyes: Eyes;
  eyeColor: string;
  /** Seconds, for fluttering belt tails and headband ends. */
  time: number;
}

export type Ctx = CanvasRenderingContext2D;

/** A tapered segment: circles at both ends joined by their outer tangents. */
export function taper(ctx: Ctx, a: Vec, b: Vec, ra: number, rb: number): void {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy) || 1e-6;
  const nx = -dy / len;
  const ny = dx / len;
  ctx.moveTo(a[0] + nx * ra, a[1] + ny * ra);
  ctx.lineTo(b[0] + nx * rb, b[1] + ny * rb);
  ctx.lineTo(b[0] - nx * rb, b[1] - ny * rb);
  ctx.lineTo(a[0] - nx * ra, a[1] - ny * ra);
  ctx.closePath();
  fillEach(ctx);
  ctx.arc(a[0], a[1], ra, 0, Math.PI * 2);
  fillEach(ctx);
  ctx.arc(b[0], b[1], rb, 0, Math.PI * 2);
  fillEach(ctx);
}

/** Fill the current shape on its own and start a new one. Filling shapes
 *  separately keeps overlaps solid (one path could cancel by winding). */
export function fillEach(ctx: Ctx): void {
  ctx.fill();
  ctx.beginPath();
}

const lerp = (a: Vec, b: Vec, t: number): Vec => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
];

/** Widening tube along a limb (sleeve / trouser leg), cut off square. */
function hem(ctx: Ctx, from: Vec, to: Vec, r0: number, r1: number): void {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const len = Math.hypot(dx, dy) || 1e-6;
  const nx = -dy / len;
  const ny = dx / len;
  ctx.moveTo(from[0] + nx * r0, from[1] + ny * r0);
  ctx.lineTo(to[0] + nx * r1, to[1] + ny * r1);
  ctx.lineTo(to[0] - nx * r1, to[1] - ny * r1);
  ctx.lineTo(from[0] - nx * r0, from[1] - ny * r0);
  ctx.closePath();
  fillEach(ctx);
}

/** A soft closed curve through the midpoints of `pts`, each point pulling it like a control point. */
function blob(ctx: Ctx, pts: Vec[]): void {
  const mid = (a: Vec, b: Vec): Vec => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const first = mid(pts[pts.length - 1]!, pts[0]!);
  ctx.moveTo(first[0], first[1]);
  pts.forEach((p, i) => {
    const m = mid(p, pts[(i + 1) % pts.length]!);
    ctx.quadraticCurveTo(p[0], p[1], m[0], m[1]);
  });
  ctx.closePath();
  fillEach(ctx);
}

export function polygon(ctx: Ctx, pts: Vec[]): void {
  pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
  ctx.closePath();
  fillEach(ctx);
}

/** `pts` (x along `angle`, y to its left, scaled by `s`) placed at `at`, as a soft blob. */
function outline(ctx: Ctx, at: Vec, angle: number, pts: readonly Vec[], s: number): void {
  const c = Math.cos(angle);
  const sn = Math.sin(angle);
  blob(
    ctx,
    pts.map(([x, y]): Vec => [at[0] + (x * c - y * sn) * s, at[1] + (x * sn + y * c) * s]),
  );
}

/**
 * A clenched fist round the hand joint, on the forearm's line: the flat back
 * of the hand on top, the knuckles squared off in front, the curled fingers
 * underneath and narrowing into the wrist.
 */
const FIST: readonly Vec[] = [
  [-0.055, 0.04],
  [0.01, 0.064],
  [0.066, 0.062],
  [0.09, 0.036],
  [0.09, -0.03],
  [0.056, -0.068],
  [-0.01, -0.064],
  [-0.055, -0.036],
];

/** A boxing glove: a padded mitt domed in front, the thumb a lobe under it. */
const GLOVE: readonly Vec[] = [
  [-0.05, 0.062],
  [0.03, 0.098],
  [0.125, 0.088],
  [0.17, 0.02],
  [0.145, -0.06],
  [0.06, -0.088],
  [-0.03, -0.078],
  [-0.062, -0.01],
];
const THUMB: readonly Vec[] = [
  [-0.035, -0.05],
  [0.05, -0.066],
  [0.06, -0.104],
  [0.0, -0.116],
  [-0.042, -0.088],
];

/**
 * A limb segment with a muscle belly: `r0` at `a`, swelling to `rm` at `t`
 * of the way (pushed `shift` to the left of a → b), narrowing to `r1` at `b`.
 */
function muscle(
  ctx: Ctx,
  a: Vec,
  b: Vec,
  r0: number,
  rm: number,
  r1: number,
  t: number,
  shift = 0,
): void {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy) || 1e-6;
  const m: Vec = [a[0] + dx * t - (dy / len) * shift, a[1] + dy * t + (dx / len) * shift];
  taper(ctx, a, m, r0, rm);
  taper(ctx, m, b, rm, r1);
}

/** Heads grow a little with the build, so big fighters aren't pin-headed. */
const headScale = (b: Build): number => b.bulk ** 0.3 * (b.head ?? 1);

/**
 * Foot outlines around the ankle's floor point, toes forward, with the
 * point the foot rolls over when the heel lifts. Karateka and kickboxers are
 * barefoot: slim, with a rounded heel. Street fighters wear chunky trainers;
 * the bruiser, the boxer and the wrestler lace up boots over the ankle.
 */
const FEET = {
  bare: {
    toe: [0.14, 0] as Vec,
    pts: [
      [-0.05, 0],
      [0.13, 0],
      [0.165, 0.012],
      [0.15, 0.035],
      [0.06, 0.058],
      [-0.015, 0.085],
      [-0.06, 0.035],
    ] as Vec[],
  },
  shoe: {
    toe: [0.17, 0] as Vec,
    pts: [
      [-0.075, 0],
      [0.18, 0],
      [0.2, 0.035],
      [0.16, 0.068],
      [0.05, 0.095],
      [-0.05, 0.11],
      [-0.08, 0.045],
    ] as Vec[],
  },
  boot: {
    toe: [0.16, 0] as Vec,
    pts: [
      [-0.075, 0],
      [0.17, 0],
      [0.19, 0.04],
      [0.14, 0.075],
      [0.06, 0.1],
      [0.05, 0.14],
      [-0.07, 0.14],
      [-0.085, 0.04],
    ] as Vec[],
  },
};

/**
 * Clothing over a limb, along its joint chain (0 = hip/shoulder, 1 =
 * knee/elbow, 2 = ankle/wrist): from, to, the radius at each end, and
 * whether the far end is cut off square like a hem (else it's round).
 */
export type Tube = readonly [from: number, to: number, r0: number, r1: number, square?: boolean];

/** How an outfit is cut: what it puts on the feet, hands, arms and legs. */
export interface Cut {
  feet: keyof typeof FEET;
  gloves?: boolean;
  /** Arm radii: upper arm at the shoulder and the elbow, forearm at the elbow and the wrist. */
  arm: readonly [number, number, number, number];
  sleeves: readonly Tube[];
  legs: readonly Tube[];
}

const BARE_ARM = [0.075, 0.06, 0.06, 0.045] as const;

const CUTS: Record<Exclude<Outfit, LazyOutfit>, Cut> = {
  // Loose sleeves past the elbow, trousers ending above the bare feet
  gi: {
    feet: 'bare',
    arm: [0.085, 0.07, 0.06, 0.045],
    sleeves: [[1, 1.68, 0.07, 0.078, true]],
    legs: [[1, 1.88, 0.09, 0.094, true]],
  },
  // Baggy sleeves to the wrist, baggy jeans bunched on the trainers
  hoodie: {
    feet: 'shoe',
    arm: [0.085, 0.075, 0.06, 0.045],
    sleeves: [[1, 1.8, 0.074, 0.068, true]],
    legs: [
      [0, 1, 0.115, 0.094],
      [1, 1.9, 0.096, 0.098, true],
    ],
  },
  // T-shirt sleeves over big arms, cargo trousers into boots
  bruiser: {
    feet: 'boot',
    arm: BARE_ARM,
    sleeves: [[0, 0.62, 0.1, 0.088, true]],
    legs: [
      [0, 1, 0.108, 0.09],
      [1, 1.8, 0.09, 0.094, true],
    ],
  },
  // Zip jacket sleeves, slim track pants tapering to the trainers
  tracksuit: {
    feet: 'shoe',
    arm: [0.08, 0.068, 0.06, 0.045],
    sleeves: [[1, 1.84, 0.068, 0.058, true]],
    legs: [
      [0, 1, 0.11, 0.086],
      [1, 1.96, 0.083, 0.068],
    ],
  },
  // Bare arms in gloves; trunks over bare legs, high boots
  boxer: {
    feet: 'boot',
    gloves: true,
    arm: BARE_ARM,
    sleeves: [],
    legs: [
      [0, 0.55, 0.13, 0.124, true],
      [1.55, 1.95, 0.064, 0.058],
    ],
  },
  // Short, wide Thai shorts flaring over the thighs; wraps round bare ankles
  thai: {
    feet: 'bare',
    arm: BARE_ARM,
    sleeves: [],
    legs: [
      [0, 0.48, 0.125, 0.16, true],
      [1.8, 1.9, 0.058, 0.058],
    ],
  },
  // A skin-tight singlet doesn't show; bare arms, tall wrestling boots
  singlet: {
    feet: 'boot',
    arm: BARE_ARM,
    sleeves: [],
    legs: [[1.5, 1.95, 0.066, 0.058]],
  },
};

/** Outfits that live in a chunk of their own (the stage bosses', boss-looks.ts). */
type LazyOutfit = 'coat' | 'hakama';

/**
 * An outfit beyond the regular cast's: its cut, what it adds around the torso
 * and on each leg. The stage bosses' looks are drawn for nobody else, so they
 * load on demand (loadBossLooks) and register here; until they arrive (or if
 * they can't), a boss is drawn in a plain gi.
 */
export interface Look {
  cut: Cut;
  torso?: (ctx: Ctx, f: Frame) => void;
  leg?: (ctx: Ctx, f: Frame, hip: Vec, knee: Vec, ankle: Vec) => void;
}

const LOOKS: Partial<Record<Outfit, Look>> = {};
const HEADGEAR: Partial<Record<Hair, (ctx: Ctx) => void>> = {};

export function addLooks(
  outfits: Partial<Record<Outfit, Look>>,
  headgear: Partial<Record<Hair, (ctx: Ctx) => void>>,
): void {
  Object.assign(LOOKS, outfits);
  Object.assign(HEADGEAR, headgear);
}

let bossLooks: Promise<void> | null = null;

/** Load the stage bosses' outfits (once; retried if it failed). */
export function loadBossLooks(): Promise<void> {
  bossLooks ??= import('./boss-looks').then(
    () => undefined,
    () => void (bossLooks = null),
  );
  return bossLooks;
}

type Chain = readonly [Vec, Vec, Vec];

/** A point `t` along a joint chain (0 = root, 1 = middle joint, 2 = end). */
const along = (c: Chain, t: number): Vec =>
  t <= 1 ? lerp(c[0], c[1], t) : lerp(c[1], c[2], t - 1);

/** Where everything is: joints per side, the feet, the torso's own frame. */
export interface Frame {
  pose: Pose;
  b: Build;
  cut: Cut;
  k: number;
  hs: number;
  /** Per side, rear first: hip → knee → ankle, shoulder → elbow → hand. */
  leg: [Chain, Chain];
  arm: [Chain, Chain];
  /** Per side: a foot-shape point (around the ankle's floor point) to where it is now. */
  foot: [(v: Vec) => Vec, (v: Vec) => Vec];
  /** Torso frame: `a` along hip → shoulder, `f` towards the front. */
  at: (a: number, f: number) => Vec;
  len: number;
  hw: number;
  sw: number;
}

type Side = 0 | 1;

/** How much of a foot's length its width shows as, seen end-on. */
const FOOT_WIDE = 0.3;

/** A foot turned `pivot` on the floor, seen from the side: how its length foreshortens, how wide it shows. */
function turnedFoot(shape: (typeof FEET)[keyof typeof FEET], pivot: number) {
  const xs = shape.pts.map((p) => p[0]);
  return {
    across: Math.cos(Math.PI * pivot),
    wide: FOOT_WIDE * (Math.max(...xs) - Math.min(...xs)) * Math.abs(Math.sin(Math.PI * pivot)),
  };
}

/**
 * How far forward the floor point under a foot's ankle goes as the foot
 * turns `pivot` on its ball, the ball staying where it is (pose units).
 */
export function pivotShift(pivot: number, b: Build): number {
  const cut = (CUTS as Partial<Record<Outfit, Cut>>)[b.outfit] ?? LOOKS[b.outfit]?.cut ?? CUTS.gi;
  const shape = FEET[cut.feet];
  const { across, wide } = turnedFoot(shape, pivot);
  return (shape.toe[0] * (1 - across) - wide / 2) * (0.85 + 0.15 * b.bulk);
}

/** A kicking foot starts turning onto its shin this high off the floor, and is there by AIR_FULL. */
const AIR_FROM = 0.1;
const AIR_FULL = 0.3;

function frame(pose: Pose, b: Build): Frame {
  const k = b.bulk;
  const cut = (CUTS as Partial<Record<Outfit, Cut>>)[b.outfit] ?? LOOKS[b.outfit]?.cut ?? CUTS.gi;
  const wide = b.shoulders ?? 1;
  // Rotation: the lead-side joints sit ahead of the rear ones in hanmi and
  // behind them once the body has turned through
  const hip = (s: number): Vec => legRoot(pose, s, k);
  const shoulder = (s: number): Vec => armRoot(pose, s, k * wide);
  const shape = FEET[cut.feet];
  const fk = 0.85 + 0.15 * k;
  // The rear heel can lift: the foot rolls over the ball, lifting the ankle.
  // A kicking foot off the floor then turns about the ankle to sit on its shin
  // (the pose's kickFoot), fully once it's AIR_FULL up
  const place = (floor: Vec, knee: Vec, lift: number, pivot = 0): ((v: Vec) => Vec) => {
    const c = Math.cos(lift);
    const sn = Math.sin(lift);
    const tx = floor[0] + shape.toe[0] * fk;
    const ty = floor[1] + shape.toe[1] * fk;
    // Turned on the floor on its ball: seen from the side the foot shortens
    // towards the ball and swings round it, showing its width side-on (the
    // floor point under the ankle swinging with the heel, `pivotShift`)
    const { across, wide } = turnedFoot(shape, pivot);
    const ball = shape.toe[0];
    const roll = ([x0, y]: Vec): Vec => {
      const x = pivot ? x0 * across + (x0 > ball ? wide : 0) : x0;
      const dx = floor[0] + x * fk - tx;
      const dy = floor[1] + y * fk - ty;
      return [tx + dx * c - dy * sn, ty + dx * sn + dy * c];
    };
    const air = Math.min(1, Math.max(0, (floor[1] - AIR_FROM) / (AIR_FULL - AIR_FROM)));
    if (pose.kickFoot === undefined || air === 0) return roll;
    const [ax, ay] = roll([0, 0.05]);
    // Standing, the foot is square to the shin; pointed, it runs along it
    const shin = Math.atan2(ay - knee[1], ax - knee[0]);
    const spin = air * (shin + Math.PI / 2 - pose.kickFoot * (Math.PI / 2));
    const c2 = Math.cos(spin);
    const s2 = Math.sin(spin);
    return (v) => {
      const [x, y] = roll(v);
      const dx = x - ax;
      const dy = y - ay;
      return [ax + dx * c2 - dy * s2, ay + dx * s2 + dy * c2];
    };
  };
  const foot: Frame['foot'] = [
    place(pose.bFoot, pose.bKnee, -(pose.heel ?? 0)),
    place(pose.fFoot, pose.fKnee, -(pose.fHeel ?? 0), pose.pivot ?? 0),
  ];
  const [hx, hy] = pose.hip;
  const [sx, sy] = pose.shoulder;
  const len = Math.hypot(sx - hx, sy - hy) || 1;
  const ux = (sx - hx) / len;
  const uy = (sy - hy) / len;
  return {
    pose,
    b,
    cut,
    k,
    hs: headScale(b),
    leg: [
      [hip(1), pose.bKnee, foot[0]([0, 0.05])],
      [hip(-1), pose.fKnee, foot[1]([0, 0.05])],
    ],
    arm: [
      [shoulder(1), pose.bElbow, pose.bHand],
      [shoulder(-1), pose.fElbow, pose.fHand],
    ],
    foot,
    at: (a, f) => [hx + ux * a + uy * f, hy + uy * a - ux * f],
    len,
    hw: 0.15 * k,
    sw: 0.19 * k * wide,
  };
}

/**
 * How far a foot's heel lifted `heel` (radians) moves its ankle (body units:
 * forward, up) as `b`'s foot rolls about its ball: where its leg then
 * reaches to.
 */
export function rolled(b: Build, heel: number): Vec {
  const cut = (CUTS as Partial<Record<Outfit, Cut>>)[b.outfit] ?? LOOKS[b.outfit]?.cut ?? CUTS.gi;
  const toe = FEET[cut.feet].toe[0] * (0.85 + 0.15 * b.bulk);
  const [c, s] = [Math.cos(heel), Math.sin(heel)];
  return [toe * (1 - c) + 0.05 * s, toe * s + 0.05 * (c - 1)];
}

/** The limbs as drawn, rear first: hip → knee → ankle and shoulder → elbow → hand. */
export function skeleton(pose: Pose, b: Build): { leg: [Chain, Chain]; arm: [Chain, Chain] } {
  const f = frame(pose, b);
  return { leg: f.leg, arm: f.arm };
}

function tubes(ctx: Ctx, chain: Chain, list: readonly Tube[], k: number): void {
  for (const [t0, t1, r0, r1, square] of list) {
    (square ? hem : taper)(ctx, along(chain, t0), along(chain, t1), r0 * k, r1 * k);
  }
}

/** A leg and what's on it, without the foot. */
function leg(ctx: Ctx, f: Frame, side: Side): void {
  const { k } = f;
  const [hip, knee, ankle] = f.leg[side];
  // The thigh, then a calf swelling behind the shin and narrowing to the ankle
  muscle(ctx, hip, knee, 0.105 * k, 0.1 * k, 0.072 * k, 0.4);
  muscle(ctx, knee, ankle, 0.072 * k, 0.078 * k, 0.045 * k, 0.3, -0.014 * k);
  tubes(ctx, f.leg[side], f.cut.legs, k);
  LOOKS[f.b.outfit]?.leg?.(ctx, f, hip, knee, ankle);
}

function foot(ctx: Ctx, f: Frame, side: Side): void {
  polygon(ctx, FEET[f.cut.feet].pts.map(f.foot[side]));
}

/** An arm and what's on it, down to the fist or glove. */
function arm(ctx: Ctx, f: Frame, side: Side): void {
  const { k, cut } = f;
  const [shoulder, elbow, hand] = f.arm[side];
  const [u0, u1, f0, f1] = cut.arm;
  const angle = Math.atan2(hand[1] - elbow[1], hand[0] - elbow[0]);
  // Fists stay fist-sized on slim builds; the forearm ends at the wrist,
  // behind the fist, so sleeves and forearm never swallow it
  const fs = Math.max(1, Math.sqrt(k));
  const wrist: Vec = [
    hand[0] - Math.cos(angle) * 0.035 * fs,
    hand[1] - Math.sin(angle) * 0.035 * fs,
  ];
  // Biceps halfway down the upper arm; the forearm thickest below the elbow,
  // slimming to the wrist
  muscle(ctx, shoulder, elbow, u0 * k, (u0 + u1) * 0.55 * k, u1 * k, 0.45);
  muscle(ctx, elbow, wrist, f0 * k, f0 * 1.08 * k, f1 * 0.9 * k, 0.25);
  tubes(ctx, [shoulder, elbow, wrist], cut.sleeves, k);
  if (cut.gloves) {
    // The glove's cuff laced over the wrist
    const back = (d: number): Vec => [hand[0] - Math.cos(angle) * d, hand[1] - Math.sin(angle) * d];
    taper(ctx, back(0.14), back(0.02), 0.06, 0.068);
    outline(ctx, hand, angle, GLOVE, 1);
    outline(ctx, hand, angle, THUMB, 1);
  } else outline(ctx, hand, angle, FIST, fs);
}

/** Torso, shoulders and what the outfit adds around them. */
function torso(ctx: Ctx, f: Frame): void {
  const { b, k, at, len, hw, sw } = f;
  // Chest forward, a flatter back, a waist and the seat, along the
  // hip → shoulder axis; a gi jacket blouses over the belt
  const belly = b.belly ?? 0;
  const back = b.back ?? 1;
  const loose = b.outfit === 'gi' ? 0.015 : 0;
  blob(ctx, [
    at(-0.08, hw * 0.95),
    at(len * 0.3, hw * 1.05 + belly + loose),
    at(len * 0.66, sw * 1.08 + belly * 0.4),
    at(len + 0.05, sw * 0.75),
    at(len + 0.06, -sw * 0.55),
    at(len * 0.72, -sw * 1.05 * back),
    at(len * 0.3, -hw * 0.9 - loose),
    at(0, -hw * 1.15),
    at(-0.09, -hw * 0.85),
  ]);
  // Shoulder caps
  const cap = 0.085 * k * (b.shoulders ?? 1);
  for (const [x, y] of [f.arm[0][0], f.arm[1][0]]) {
    ctx.moveTo(x + cap, y);
    ctx.arc(x, y, cap, 0, Math.PI * 2);
    fillEach(ctx);
  }
  switch (b.outfit) {
    case 'gi':
      // Jacket skirt flaring below the belt, collar standing round the neck
      polygon(ctx, [at(0, -hw * 1.05), at(0, hw * 1.05), at(-0.2, hw * 1.3), at(-0.2, -hw * 1.3)]);
      taper(ctx, at(len - 0.02, -0.012), at(len + 0.09, -0.03), 0.085, 0.066);
      break;
    case 'hoodie':
      // The hem sits low on the hips, a step out from the jeans
      polygon(ctx, [
        at(0.04, -hw * 1.12),
        at(0.04, hw * 1.16),
        at(-0.12, hw * 1.14),
        at(-0.12, -hw * 1.1),
      ]);
      break;
    case 'tracksuit':
      // Zipped up to a high collar
      taper(ctx, at(len - 0.02, 0), at(len + 0.12, -0.012), 0.088, 0.074);
      break;
    case 'boxer':
    case 'thai':
      // Wide waistband, high on the hips
      polygon(ctx, [
        at(0.1, -hw * 1.12),
        at(0.1, hw * 1.16),
        at(-0.05, hw * 1.16),
        at(-0.05, -hw * 1.12),
      ]);
      break;
    default:
      LOOKS[b.outfit]?.torso?.(ctx, f);
  }
  // A long belt's tails hang from the knot, swinging below the jacket
  if (b.tails) {
    const knot = at(0.07, hw * knotAt(f.pose));
    for (const [dx, long] of [
      [0.05, 1],
      [-0.025, 1.1],
    ] as const) {
      taper(ctx, knot, [knot[0] + dx, knot[1] - b.tails * long], 0.032, 0.026);
    }
  }
}

/** Neck and head, with hair or headgear. */
function head(ctx: Ctx, f: Frame, time: number): void {
  const { pose, b, at, len, sw, hs } = f;
  const [hx, hy] = pose.head;
  // Neck, with the trapezius sloping into it on thick-necked builds
  const neck = b.neck ?? 1;
  taper(
    ctx,
    lerp(pose.shoulder, pose.head, 0.1),
    lerp(pose.shoulder, pose.head, 0.75),
    0.06 * neck,
    0.055 * neck,
  );
  if (neck > 1) {
    polygon(ctx, [
      at(len, sw * 0.7),
      lerp(pose.shoulder, pose.head, 0.25 + 0.3 * (neck - 1)),
      at(len, -sw * 0.8),
    ]);
  }
  // A raised hood falls from the back of the head to the neck
  if (b.hair === 'hood') {
    taper(
      ctx,
      [hx - 0.07 * hs, hy - 0.05 * hs],
      at(len + 0.02, -sw * 0.35),
      0.085 * hs,
      0.075 * f.k,
    );
  }
  // Head, in its own frame: a round skull with a jaw and a nose so the
  // silhouette shows which way it faces
  inHead(ctx, f, () => {
    ctx.moveTo(0.125, 0);
    ctx.arc(0, 0, 0.125, 0, Math.PI * 2);
    fillEach(ctx);
    polygon(ctx, [
      [-0.04, -0.06],
      [0.015, -0.125],
      [0.075, -0.132],
      [0.122, -0.05],
    ]);
    polygon(ctx, [
      [0.115, 0.02],
      [0.152, -0.018],
      [0.118, -0.035],
    ]);
    hair(ctx, b.hair, time);
  });
}

/** Run `draw` in the head's own frame (radius 0.125 at the origin, facing right). */
function inHead(ctx: Ctx, f: Frame, draw: () => void): void {
  ctx.save();
  ctx.translate(f.pose.head[0], f.pose.head[1]);
  ctx.scale(f.hs, f.hs);
  draw();
  ctx.restore();
}

/** The whole body as one ink shape (everything the same colour). */
function body(ctx: Ctx, f: Frame, time: number, arms = true): void {
  ctx.beginPath();
  for (const side of [0, 1] as const) {
    leg(ctx, f, side);
    foot(ctx, f, side);
  }
  torso(ctx, f);
  head(ctx, f, time);
  if (!arms) return;
  arm(ctx, f, 0);
  arm(ctx, f, 1);
}

/** A row of spikes over the crown: the punk kickboxer's silhouette. */
function mohawk(ctx: Ctx): void {
  for (let i = 0; i < 5; i++) {
    const a = Math.PI * (0.25 + i * 0.13);
    const bx = Math.cos(a) * 0.11;
    const by = Math.sin(a) * 0.11;
    polygon(ctx, [
      [bx - 0.03, by],
      [bx + Math.cos(a) * 0.1, by + Math.sin(a) * 0.1 + 0.03],
      [bx + 0.03, by - 0.01],
    ]);
  }
}

/** Hair and headgear around a head at the origin (radius 0.125, facing right). */
function hair(ctx: Ctx, hair: Hair, time: number): void {
  switch (hair) {
    case 'bun':
      // Bun tucked against the back of the head
      ctx.moveTo(-0.1 + 0.06, 0.07);
      ctx.arc(-0.1, 0.07, 0.06, 0, Math.PI * 2);
      break;
    case 'hood':
      // Pulled up over the skull, its edge peaking over the brow; jaw and
      // nose stay out in front of it
      ctx.moveTo(-0.025 + 0.148, 0.02);
      ctx.arc(-0.025, 0.02, 0.148, 0, Math.PI * 2);
      fillEach(ctx);
      polygon(ctx, [
        [0.02, 0.15],
        [0.15, 0.085],
        [0.142, 0.052],
        [0.05, 0.07],
      ]);
      break;
    case 'beard':
      // Bald, with a full beard jutting forward and down past the chin
      blob(ctx, [
        [-0.06, -0.01],
        [0.06, -0.06],
        [0.155, -0.07],
        [0.17, -0.17],
        [0.1, -0.27],
        [-0.01, -0.23],
        [-0.065, -0.11],
      ]);
      break;
    case 'capBack':
      // Cap on backwards: the brim sticks out behind, tipped down
      ctx.moveTo(-0.05, 0.07);
      ctx.lineTo(-0.26, 0.03);
      ctx.lineTo(-0.265, 0.07);
      ctx.lineTo(0, 0.13);
      ctx.closePath();
      fillEach(ctx);
      ctx.moveTo(0.14, 0.05);
      ctx.ellipse(0, 0.05, 0.14, 0.1, 0, Math.PI, 0, true);
      break;
    case 'cap':
      ctx.moveTo(0.05, 0.06);
      ctx.lineTo(0.27, 0.05);
      ctx.lineTo(0.27, 0.09);
      ctx.lineTo(0, 0.13);
      ctx.closePath();
      fillEach(ctx);
      ctx.moveTo(0.14, 0.05);
      ctx.ellipse(0, 0.05, 0.14, 0.1, 0, Math.PI, 0, true);
      break;
    case 'flattop':
      // Squared-off crew cut: the wrestler's silhouette
      polygon(ctx, [
        [-0.12, 0.05],
        [-0.1, 0.16],
        [0.1, 0.16],
        [0.12, 0.05],
      ]);
      break;
    case 'mohawk':
      mohawk(ctx);
      break;
    case 'headband': {
      // A band round the forehead, knotted at the back; two cloth tails ripple out behind
      polygon(ctx, [
        [-0.132, 0.035],
        [0.128, 0.035],
        [0.12, 0.078],
        [-0.125, 0.078],
      ]);
      const knot: Vec = [-0.13, 0.056];
      ctx.moveTo(knot[0] + 0.032, knot[1]);
      ctx.arc(knot[0], knot[1], 0.032, 0, Math.PI * 2);
      fillEach(ctx);
      for (const [phase, droop] of [
        [0, -0.02],
        [1.9, -0.055],
      ] as const) {
        let p = knot;
        for (let i = 1; i <= 3; i++) {
          const q: Vec = [
            knot[0] - i * 0.075,
            knot[1] + droop * i + Math.sin(time * 9 - i * 1.3 + phase) * 0.016 * i,
          ];
          taper(ctx, p, q, 0.022 - i * 0.003, 0.019 - i * 0.003);
          p = q;
        }
      }
      break;
    }
    default:
      HEADGEAR[hair]?.(ctx);
  }
  fillEach(ctx);
}

/** Belt with a knot and two tails that flutter; the only colour on a karateka. */
/**
 * Where the belt's knot sits across the front of the belt (0 = the middle,
 * 1 = the front edge): it follows the hips' turn, in the middle when they're
 * side-on (hanmi, the belly toward us), at the front edge once they're square
 * to the opponent or turned through, where it stands out from the outline.
 */
function knotAt(pose: Pose): number {
  if (pose.knot !== undefined) return pose.knot;
  const turn = pose.turn ?? 0;
  return turn >= 0 ? 1 : Math.cos((turn * Math.PI) / 2);
}

function belt(ctx: Ctx, pose: Pose, b: Build, color: string, time: number): void {
  const [hx, hy] = pose.hip;
  const [sx, sy] = pose.shoulder;
  const len = Math.hypot(sx - hx, sy - hy) || 1;
  const nx = (sy - hy) / len;
  const ny = -(sx - hx) / len;
  const ux = (sx - hx) / len;
  const uy = (sy - hy) / len;
  const hw = 0.15 * b.bulk * 1.08;
  const cx = hx + ux * 0.07;
  const cy = hy + uy * 0.07;
  const th = 0.055;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(cx - nx * hw - (ux * th) / 2, cy - ny * hw - (uy * th) / 2);
  ctx.lineTo(cx + nx * hw - (ux * th) / 2, cy + ny * hw - (uy * th) / 2);
  ctx.lineTo(cx + nx * hw + (ux * th) / 2, cy + ny * hw + (uy * th) / 2);
  ctx.lineTo(cx - nx * hw + (ux * th) / 2, cy - ny * hw + (uy * th) / 2);
  ctx.closePath();
  // The knot shows the hips' turn, tails hanging and swaying from it
  const kx = cx + nx * hw * knotAt(pose);
  const ky = cy + ny * hw * knotAt(pose);
  ctx.moveTo(kx + 0.035, ky);
  ctx.arc(kx, ky, 0.035, 0, Math.PI * 2);
  const sway = Math.sin(time * 5) * 0.025;
  for (const [dx, lenT] of [
    [0.05, 0.2],
    [-0.02, 0.22],
  ] as const) {
    ctx.moveTo(kx - 0.012, ky);
    ctx.lineTo(kx + dx + sway - 0.015, ky - lenT);
    ctx.lineTo(kx + dx + sway + 0.02, ky - lenT);
    ctx.lineTo(kx + 0.012, ky);
    ctx.closePath();
  }
  ctx.fill();
}

function eyes(ctx: Ctx, pose: Pose, b: Build, eyes: Eyes, color: string): void {
  if (eyes === 'none') return;
  ctx.save();
  ctx.translate(pose.head[0], pose.head[1]);
  const hs = headScale(b);
  ctx.scale(hs, hs);
  const hx = 0;
  const hy = 0;
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineWidth = 0.022;
  for (const ex of [0.035, 0.095]) {
    ctx.beginPath();
    if (eyes === 'hurt') {
      ctx.moveTo(hx + ex - 0.025, hy + 0.02);
      ctx.lineTo(hx + ex + 0.025, hy - 0.01);
      ctx.moveTo(hx + ex - 0.025, hy - 0.01);
      ctx.lineTo(hx + ex + 0.025, hy + 0.02);
      ctx.stroke();
    } else {
      // Slits: level when calm, slanted down to the front when angry
      const tilt = eyes === 'angry' ? 0.02 : 0;
      ctx.moveTo(hx + ex - 0.025, hy + 0.015 + tilt);
      ctx.lineTo(hx + ex + 0.025, hy + 0.015 - tilt);
      ctx.lineTo(hx + ex + 0.02, hy - 0.005 - tilt);
      ctx.lineTo(hx + ex - 0.02, hy - 0.005 + tilt);
      ctx.closePath();
      ctx.fill();
    }
  }
  ctx.restore();
}

/**
 * Draw a fighter silhouette into `ctx` with its feet at (x, groundY).
 * `parts` splits the solid shape from the coloured details so the caller can
 * tint the shape on its own (rim light) before adding belt and eyes.
 */
/** Drawing calls the outline recorder can't follow (none, as long as it keeps up with the rig). */
export const UNFOLLOWED = new Set<string>();

/**
 * A stand-in canvas keeping the outline of every shape filled into it (curves
 * as short straight runs), to measure what's drawn exactly as it's drawn.
 */
function recorder(): { ctx: Ctx; shapes: Vec[][] } {
  const shapes: Vec[][] = [];
  let m = [1, 0, 0, 1, 0, 0];
  const stack: number[][] = [];
  let path: Vec[][] = [];
  let cur: Vec[] = [];
  const tx = (x: number, y: number): Vec => [
    m[0]! * x + m[2]! * y + m[4]!,
    m[1]! * x + m[3]! * y + m[5]!,
  ];
  const to = (p: Vec): void => {
    if (!cur.length) path.push(cur);
    cur.push(p);
  };
  const turn = (
    x: number,
    y: number,
    rx: number,
    ry: number,
    rot: number,
    a0: number,
    a1: number,
    ccw = false,
  ): void => {
    let span = a1 - a0;
    if (!ccw && span < 0) span = (span % (2 * Math.PI)) + 2 * Math.PI;
    if (ccw && span > 0) span = (span % (2 * Math.PI)) - 2 * Math.PI;
    const n = Math.max(2, Math.ceil((Math.abs(span) / (2 * Math.PI)) * 28));
    const [c, sn] = [Math.cos(rot), Math.sin(rot)];
    for (let i = 0; i <= n; i++) {
      const a = a0 + (span * i) / n;
      const [ex, ey] = [rx * Math.cos(a), ry * Math.sin(a)];
      to(tx(x + ex * c - ey * sn, y + ex * sn + ey * c));
    }
  };
  const ctx = {
    save: () => void stack.push([...m]),
    restore: () => void (m = stack.pop() ?? m),
    translate: (x: number, y: number) => void (m = [m[0]!, m[1]!, m[2]!, m[3]!, ...tx(x, y)]),
    scale: (x: number, y: number) =>
      void (m = [m[0]! * x, m[1]! * x, m[2]! * y, m[3]! * y, m[4]!, m[5]!]),
    rotate: (a: number) => {
      const [c, sn] = [Math.cos(a), Math.sin(a)];
      m = [
        m[0]! * c + m[2]! * sn,
        m[1]! * c + m[3]! * sn,
        m[2]! * c - m[0]! * sn,
        m[3]! * c - m[1]! * sn,
        m[4]!,
        m[5]!,
      ];
    },
    beginPath: () => void ((path = []), (cur = [])),
    moveTo: (x: number, y: number) => void ((cur = []), to(tx(x, y))),
    lineTo: (x: number, y: number) => to(tx(x, y)),
    closePath: () => void (cur = []),
    quadraticCurveTo: (cx: number, cy: number, x: number, y: number) => {
      const p0 = cur[cur.length - 1] ?? tx(cx, cy);
      const [c, p1] = [tx(cx, cy), tx(x, y)];
      for (let i = 1; i <= 8; i++) {
        const t = i / 8;
        const [u, v, w] = [(1 - t) ** 2, 2 * t * (1 - t), t * t];
        to([u * p0[0] + v * c[0] + w * p1[0], u * p0[1] + v * c[1] + w * p1[1]]);
      }
    },
    arc: (x: number, y: number, r: number, a0: number, a1: number, ccw?: boolean) =>
      turn(x, y, r, r, 0, a0, a1, ccw),
    ellipse: (
      x: number,
      y: number,
      rx: number,
      ry: number,
      rot: number,
      a0: number,
      a1: number,
      ccw?: boolean,
    ) => turn(x, y, rx, ry, rot, a0, a1, ccw),
    fill: () => {
      for (const sub of path) if (sub.length > 2) shapes.push(sub);
    },
  };
  // Anything else a drawing calls leaves no outline (noted, so a test can
  // tell if a shape goes unmeasured); colours and the like are just set
  const any = new Proxy(ctx, {
    get: (t, k) => {
      if (k in t) return t[k as keyof typeof t];
      UNFOLLOWED.add(String(k));
      return () => undefined;
    },
    set: () => true,
  });
  return { ctx: any as unknown as Ctx, shapes };
}

/** Paint for measuring a silhouette: nothing in it is ever seen. */
const MEASURE = { fill: '#000', eyes: 'none' as const, eyeColor: '#000', time: 0 };

/**
 * How far forward of its spot `pose`'s body reaches within `band` of height
 * `y` (pose units, facing right; -Infinity if nothing's there): its outline
 * exactly as drawn — legs, feet, torso, head, hair and outfit — but the arms,
 * which a strike goes past (in a side view a guard hand overlaps the face it
 * guards). A strike as thick as the band meets the first of it.
 */
export function bodyFront(pose: Pose, build: Build, y: number, band = 0): number {
  const { ctx, shapes } = recorder();
  drawFighter(ctx, 0, 0, 1 / build.scale, 1, pose, build, MEASURE, { parts: 'trunk' });
  const [lo, hi] = [y - band, y + band];
  let front = -Infinity;
  for (const shape of shapes) {
    for (let i = 0; i < shape.length; i++) {
      const [a, c] = [shape[i]!, shape[(i + 1) % shape.length]!];
      // (drawn y runs down: -y is up); an edge's furthest point within the band
      const [ya, yc] = [-a[1], -c[1]];
      if (Math.max(ya, yc) < lo || Math.min(ya, yc) > hi) continue;
      const along = (h: number): number =>
        ya === yc ? Math.max(a[0], c[0]) : a[0] + ((c[0] - a[0]) * (h - ya)) / (yc - ya);
      const [h0, h1] = [Math.max(lo, Math.min(ya, yc)), Math.min(hi, Math.max(ya, yc))];
      front = Math.max(front, along(h0), along(h1));
    }
  }
  return front;
}

/**
 * Where a foot's ball (what a mae-geri lands with) or heel is as drawn, in
 * pose units: a kicking foot in the air has turned onto its shin.
 */
export function footPoint(
  pose: Pose,
  build: Build,
  leg: 'front' | 'rear',
  at: 'ball' | 'heel',
): Vec {
  const f = frame(pose, build);
  const shape = FEET[f.cut.feet];
  return f.foot[leg === 'front' ? 1 : 0](at === 'ball' ? shape.toe : shape.pts[0]!);
}

export function drawFighter(
  ctx: Ctx,
  x: number,
  groundY: number,
  ppu: number,
  facing: 1 | -1,
  pose: Pose,
  build: Build,
  paint: Paint,
  opts: { rotation?: number; parts?: 'all' | 'body' | 'trunk' | 'details' } = {},
): void {
  const k = ppu * build.scale;
  const parts = opts.parts ?? 'all';
  const f = frame(pose, build);
  ctx.save();
  ctx.translate(x, groundY);
  ctx.scale(facing * k, -k);
  if (opts.rotation) {
    ctx.translate(pose.hip[0], pose.hip[1]);
    ctx.rotate(opts.rotation);
    ctx.translate(-pose.hip[0], -pose.hip[1]);
  }
  if (parts !== 'details') {
    ctx.fillStyle = paint.fill;
    body(ctx, f, paint.time, parts !== 'trunk');
  }
  if (parts === 'all' || parts === 'details') {
    if (paint.belt) belt(ctx, pose, build, paint.belt, paint.time);
    eyes(ctx, pose, build, paint.eyes, paint.eyeColor);
  }
  ctx.restore();
}
